export type DeepsearchDetailItem = {
  asin: string
  title: string
  share: string
  conversion: string
}

export type DeepsearchResultHistoryPoint = {
  date: string
  frequency: number | null
  searchVolume: number | null
  rank?: number | null
  changePercent?: number | null
  clickShare?: number | null
  conversionShare?: number | null
  trendStatus?: string | null
  dataQualityFlag?: string | null
}

export type TrendKey = 'RISING' | 'FALLING' | 'STABLE' | 'NEW' | 'OUT' | 'VOLATILE' | 'NO_DATA'

export type DeepsearchResultRow = {
  id: string
  frequency: number | null
  algorithmRank: number | null
  currentRank: number | null
  dailyRank: number | null
  weeklyRank: number | null
  monthlyRank: number | null
  keyword: string
  dailyChange: string
  weeklyChange: string
  monthlyChange: string
  trend: TrendKey
  volume: string
  topCategory: string
  orders: number
  date: string
  series: string
  detailItems: DeepsearchDetailItem[]
  history: DeepsearchResultHistoryPoint[]
  /** #472: ilk 3 ürünün ham tıklama payı (yüzde ölçeği); Pazar Hakimiyeti kolonu kullanır. */
  clickShares?: [number | null, number | null, number | null]
  /** #472: Pazar Hakimiyeti ayrıntı balonu — ilk 3 ürünün adı/payları (yüzde ölçeği, ham). */
  dominanceProducts?: Array<{
    asin: string | null
    title: string | null
    clickShare: number | null
    conversionShare: number | null
  }>
  matchedAsins?: string[]  // Batch ASIN aramasında eşleşen ASIN'ler
}

const makeHistory = ({
  startDate,
  days,
  frequencyBase,
  frequencyTrend,
  frequencyVolatility,
  searchBase,
  searchTrend,
  searchVolatility,
}: {
  startDate: string
  days: number
  frequencyBase: number
  frequencyTrend: number
  frequencyVolatility: number
  searchBase: number
  searchTrend: number
  searchVolatility: number
}): DeepsearchResultHistoryPoint[] => {
  const start = new Date(startDate)
  const history: DeepsearchResultHistoryPoint[] = []

  for (let i = 0; i < days; i += 1) {
    const current = new Date(start)
    current.setDate(start.getDate() + i)

    const seasonal = Math.sin((i / 30) * Math.PI * 2)
    const longSeasonal = Math.cos((i / 120) * Math.PI * 2)

    const frequencyValue =
      frequencyBase +
      frequencyTrend * i +
      frequencyVolatility * seasonal +
      frequencyVolatility * 0.45 * longSeasonal

    const searchValue =
      searchBase +
      searchTrend * i +
      searchVolatility * seasonal +
      searchVolatility * 0.3 * longSeasonal

    history.push({
      date: current.toISOString().slice(0, 10),
      frequency: Math.max(0, Math.round(frequencyValue)),
      searchVolume: Math.max(0, Math.round(searchValue)),
    })
  }

  return history
}

const numberFormatter = new Intl.NumberFormat('tr-TR')
const formatVolume = (value: number) => numberFormatter.format(Math.max(0, Math.round(value)))
const getLatestPoint = (history: DeepsearchResultHistoryPoint[]) =>
  history[history.length - 1] ?? { frequency: 0, searchVolume: 0 }

type HistoryConfig = Parameters<typeof makeHistory>[0]

const HISTORY_CONFIGS: Record<string, HistoryConfig> = {
  'row-1': {
    startDate: '2023-01-01',
    days: 730,
    frequencyBase: 72,
    frequencyTrend: 0.0534,
    frequencyVolatility: 20,
    searchBase: 58567,
    searchTrend: 53.48,
    searchVolatility: 21474,
  },
  'row-2': {
    startDate: '2023-01-13',
    days: 730,
    frequencyBase: 53,
    frequencyTrend: 0.0384,
    frequencyVolatility: 15,
    searchBase: 38526,
    searchTrend: 35.18,
    searchVolatility: 14126,
  },
  'row-3': {
    startDate: '2023-01-25',
    days: 730,
    frequencyBase: 32,
    frequencyTrend: 0.0233,
    frequencyVolatility: 9,
    searchBase: 34802,
    searchTrend: 31.78,
    searchVolatility: 12761,
  },
  'row-4': {
    startDate: '2023-02-06',
    days: 730,
    frequencyBase: 51,
    frequencyTrend: 0.037,
    frequencyVolatility: 14,
    searchBase: 25668,
    searchTrend: 23.44,
    searchVolatility: 9412,
  },
  'row-5': {
    startDate: '2023-02-18',
    days: 730,
    frequencyBase: 38,
    frequencyTrend: 0.0274,
    frequencyVolatility: 10,
    searchBase: 17376,
    searchTrend: 15.87,
    searchVolatility: 6371,
  },
  'row-6': {
    startDate: '2023-03-02',
    days: 730,
    frequencyBase: 27,
    frequencyTrend: 0.0192,
    frequencyVolatility: 7,
    searchBase: 18852,
    searchTrend: 17.22,
    searchVolatility: 6912,
  },
  'row-7': {
    startDate: '2023-03-14',
    days: 730,
    frequencyBase: 43,
    frequencyTrend: 0.0315,
    frequencyVolatility: 12,
    searchBase: 20124,
    searchTrend: 18.38,
    searchVolatility: 7379,
  },
  'row-8': {
    startDate: '2023-03-26',
    days: 730,
    frequencyBase: 34,
    frequencyTrend: 0.026,
    frequencyVolatility: 10,
    searchBase: 14646,
    searchTrend: 13.38,
    searchVolatility: 5370,
  },
  'row-9': {
    startDate: '2023-04-07',
    days: 730,
    frequencyBase: 47,
    frequencyTrend: 0.0342,
    frequencyVolatility: 13,
    searchBase: 23922,
    searchTrend: 21.85,
    searchVolatility: 8771,
  },
  'row-10': {
    startDate: '2023-04-19',
    days: 730,
    frequencyBase: 24,
    frequencyTrend: 0.0178,
    frequencyVolatility: 7,
    searchBase: 15672,
    searchTrend: 14.31,
    searchVolatility: 5746,
  },
  'row-11': {
    startDate: '2023-05-01',
    days: 730,
    frequencyBase: 40,
    frequencyTrend: 0.0301,
    frequencyVolatility: 11,
    searchBase: 11058,
    searchTrend: 10.1,
    searchVolatility: 4055,
  },
  'row-12': {
    startDate: '2023-05-13',
    days: 730,
    frequencyBase: 29,
    frequencyTrend: 0.0219,
    frequencyVolatility: 8,
    searchBase: 13728,
    searchTrend: 12.54,
    searchVolatility: 5034,
  },
  'row-13': {
    startDate: '2023-05-25',
    days: 730,
    frequencyBase: 48,
    frequencyTrend: 0.0356,
    frequencyVolatility: 13,
    searchBase: 20856,
    searchTrend: 19.05,
    searchVolatility: 7647,
  },
  'row-14': {
    startDate: '2023-06-06',
    days: 730,
    frequencyBase: 34,
    frequencyTrend: 0.0247,
    frequencyVolatility: 9,
    searchBase: 11964,
    searchTrend: 10.93,
    searchVolatility: 4387,
  },
  'row-15': {
    startDate: '2023-06-18',
    days: 730,
    frequencyBase: 41,
    frequencyTrend: 0.0301,
    frequencyVolatility: 11,
    searchBase: 16566,
    searchTrend: 15.13,
    searchVolatility: 6074,
  },
  'row-16': {
    startDate: '2023-06-30',
    days: 730,
    frequencyBase: 45,
    frequencyTrend: 0.0329,
    frequencyVolatility: 12,
    searchBase: 21912,
    searchTrend: 20.01,
    searchVolatility: 8034,
  },
  'row-17': {
    startDate: '2023-07-12',
    days: 730,
    frequencyBase: 36,
    frequencyTrend: 0.026,
    frequencyVolatility: 10,
    searchBase: 14262,
    searchTrend: 13.02,
    searchVolatility: 5229,
  },
  'row-18': {
    startDate: '2023-07-24',
    days: 730,
    frequencyBase: 39,
    frequencyTrend: 0.0288,
    frequencyVolatility: 11,
    searchBase: 12984,
    searchTrend: 11.86,
    searchVolatility: 4761,
  },
  'row-19': {
    startDate: '2023-08-05',
    days: 730,
    frequencyBase: 30,
    frequencyTrend: 0.0219,
    frequencyVolatility: 8,
    searchBase: 10524,
    searchTrend: 9.61,
    searchVolatility: 3859,
  },
  'row-20': {
    startDate: '2023-08-17',
    days: 730,
    frequencyBase: 25,
    frequencyTrend: 0.0178,
    frequencyVolatility: 7,
    searchBase: 11538,
    searchTrend: 10.54,
    searchVolatility: 4231,
  },
  'row-21': {
    startDate: '2023-08-29',
    days: 730,
    frequencyBase: 37,
    frequencyTrend: 0.0274,
    frequencyVolatility: 10,
    searchBase: 17688,
    searchTrend: 16.15,
    searchVolatility: 6486,
  },
  'row-22': {
    startDate: '2023-09-10',
    days: 730,
    frequencyBase: 29,
    frequencyTrend: 0.0205,
    frequencyVolatility: 8,
    searchBase: 19866,
    searchTrend: 18.14,
    searchVolatility: 7284,
  },
  'row-23': {
    startDate: '2023-09-22',
    days: 730,
    frequencyBase: 23,
    frequencyTrend: 0.0164,
    frequencyVolatility: 6,
    searchBase: 8892,
    searchTrend: 8.12,
    searchVolatility: 3260,
  },
  'row-24': {
    startDate: '2023-10-04',
    days: 730,
    frequencyBase: 31,
    frequencyTrend: 0.0233,
    frequencyVolatility: 9,
    searchBase: 12384,
    searchTrend: 11.31,
    searchVolatility: 4541,
  },
  'row-25': {
    startDate: '2023-10-16',
    days: 730,
    frequencyBase: 34,
    frequencyTrend: 0.0247,
    frequencyVolatility: 9,
    searchBase: 19068,
    searchTrend: 17.41,
    searchVolatility: 6992,
  },
}

const historyByRowId = Object.fromEntries(
  Object.entries(HISTORY_CONFIGS).map(([id, config]) => [id, makeHistory(config)])
) as Record<string, DeepsearchResultHistoryPoint[]>

const buildRow = (
  id: string,
  row: Omit<
    DeepsearchResultRow,
    'id' | 'frequency' | 'volume' | 'history' | 'algorithmRank' | 'currentRank' | 'dailyRank' | 'weeklyRank' | 'monthlyRank'
  >,
): DeepsearchResultRow => {
  const history = historyByRowId[id] ?? []
  const latest = getLatestPoint(history)
  const sequentialRank = Number.parseInt(id.replace('row-', ''), 10)
  return {
    id,
    frequency: latest.frequency,
    algorithmRank: Number.isFinite(sequentialRank) ? sequentialRank : latest.frequency,
    currentRank: latest.frequency,
    dailyRank: latest.frequency,
    weeklyRank: latest.frequency,
    monthlyRank: latest.frequency,
    volume: formatVolume(latest.searchVolume),
    history,
    ...row,
  }
}

export const deepsearchRowsMock: DeepsearchResultRow[] = [
  buildRow('row-1', {
    keyword: 'nad resveratrol supplement',
    dailyChange: '+91.38%',
    weeklyChange: '0%',
    monthlyChange: '0%',
    trend: 'RISING',
    topCategory: 'Health & Personal Care',
    orders: 3,
    date: '27.09.2025',
    series: 'Sales series',
    detailItems: [
      { asin: 'B0FP2SM9PP', title: 'LUX&HEALTH NAD+', share: '97.6%', conversion: '0.01%' },
      { asin: 'B0F85C1CD2', title: 'Cata-Kor NMN™ Su', share: '94.0%', conversion: '0.08%' },
      { asin: 'B0CMN2TRNF', title: 'NAD Supplement 8', share: '90.2%', conversion: '0.29%' },
    ],
  }),
  buildRow('row-2', {
    keyword: 'neck massager',
    dailyChange: '+88.76%',
    weeklyChange: '+102%',
    monthlyChange: '+18%',
    trend: 'RISING',
    topCategory: 'Health & Personal Care',
    orders: 2,
    date: '27.09.2025',
    series: 'Trend insight',
    detailItems: [
      { asin: 'B0HP5L1D22', title: 'Smart Neck Massager', share: '82.1%', conversion: '0.91%' },
      { asin: 'B0HF6P0K14', title: 'Heated Relief Set', share: '54.0%', conversion: '0.35%' },
      { asin: 'B0CF20MZ61', title: 'Air Pulse Cervical Massager', share: '36.4%', conversion: '0.17%' },
    ],
  }),
  buildRow('row-3', {
    keyword: 'tulsa king',
    dailyChange: '+87.10%',
    weeklyChange: '+55%',
    monthlyChange: '+12%',
    trend: 'STABLE',
    topCategory: 'Digital Video',
    orders: 1,
    date: '26.09.2025',
    series: 'Frequency history',
    detailItems: [
      { asin: 'B0MR4T2PK4', title: 'Tulsa King Season 2', share: '71.3%', conversion: '3.41%' },
      { asin: 'B0MN8DZ9K1', title: 'Tulsa King Soundtrack Vinyl', share: '46.2%', conversion: '1.12%' },
      { asin: 'B0CP3V5P93', title: 'Sylvester Stallone Exclusive Poster', share: '28.9%', conversion: '0.64%' },
    ],
  }),
  buildRow('row-4', {
    keyword: 'wireless charging pad',
    dailyChange: '+64.55%',
    weeklyChange: '+31%',
    monthlyChange: '+9%',
    trend: 'RISING',
    topCategory: 'Electronics',
    orders: 4,
    date: '27.09.2025',
    series: 'Growth monitor',
    detailItems: [
      { asin: 'B0CX2N8PAD', title: 'Sensei Dual-Coil Charging Pad', share: '88.4%', conversion: '0.46%' },
      { asin: 'B0BQ5WLANQ', title: 'MagSafe Nightstand Dock', share: '73.2%', conversion: '0.38%' },
      { asin: 'B0BL9CHRG5', title: 'Qi2 Travel Charger', share: '61.7%', conversion: '0.29%' },
    ],
  }),
  buildRow('row-5', {
    keyword: 'cold plunge tub',
    dailyChange: '+52.18%',
    weeklyChange: '+27%',
    monthlyChange: '+14%',
    trend: 'RISING',
    topCategory: 'Sports & Outdoors',
    orders: 3,
    date: '26.09.2025',
    series: 'Recovery insights',
    detailItems: [
      { asin: 'B0CP7PLNGE', title: 'PolarRevive Plunge Tub', share: '69.8%', conversion: '0.73%' },
      { asin: 'B0BG2CHILL', title: 'ArcticFlex Portable Ice Bath', share: '54.2%', conversion: '0.42%' },
      { asin: 'B0FX1SHLD0', title: 'Sensei Thermal Lid Kit', share: '37.4%', conversion: '0.21%' },
    ],
  }),
  buildRow('row-6', {
    keyword: 'smart desk treadmill',
    dailyChange: '+47.83%',
    weeklyChange: '+19%',
    monthlyChange: '+8%',
    trend: 'STABLE',
    topCategory: 'Home & Kitchen',
    orders: 2,
    date: '25.09.2025',
    series: 'Product adoption',
    detailItems: [
      { asin: 'B0TR2PACE0', title: 'StrideFlex Folding Desk Treadmill', share: '63.5%', conversion: '0.64%' },
      { asin: 'B0TG1PACE3', title: 'Sensei QuietWalk Pro', share: '51.8%', conversion: '0.52%' },
      { asin: 'B0LM2BALNC', title: 'ActiveMat Under Desk Cushion', share: '35.7%', conversion: '0.18%' },
    ],
  }),
  buildRow('row-7', {
    keyword: 'matcha collagen powder',
    dailyChange: '+59.21%',
    weeklyChange: '+24%',
    monthlyChange: '+10%',
    trend: 'RISING',
    topCategory: 'Grocery',
    orders: 4,
    date: '27.09.2025',
    series: 'Ingredient tracker',
    detailItems: [
      { asin: 'B0MC0MATCH', title: 'Sensei Matcha Collagen Blend', share: '72.8%', conversion: '0.65%' },
      { asin: 'B0MT9COLGN', title: 'Kyoto Vital Collagen Matcha', share: '58.4%', conversion: '0.44%' },
      { asin: 'B0ML2PUREE', title: 'Green Ritual Matcha Mix', share: '41.9%', conversion: '0.29%' },
    ],
  }),
  buildRow('row-8', {
    keyword: 'travel makeup bag',
    dailyChange: '+38.12%',
    weeklyChange: '+18%',
    monthlyChange: '+7%',
    trend: 'RISING',
    topCategory: 'Beauty & Personal Care',
    orders: 5,
    date: '25.09.2025',
    series: 'Accessory growth',
    detailItems: [
      { asin: 'B0TRVLMAKE', title: 'Sensei Modular Makeup Bag', share: '64.1%', conversion: '0.51%' },
      { asin: 'B0GLMIRROR', title: 'GlowLite LED Travel Case', share: '52.3%', conversion: '0.38%' },
      { asin: 'B0BLPALETT', title: 'Palette Keeper Cosmetic Kit', share: '39.4%', conversion: '0.26%' },
    ],
  }),
  buildRow('row-9', {
    keyword: 'portable projector 4k',
    dailyChange: '+44.26%',
    weeklyChange: '+21%',
    monthlyChange: '+11%',
    trend: 'RISING',
    topCategory: 'Electronics',
    orders: 4,
    date: '27.09.2025',
    series: 'Entertainment pulse',
    detailItems: [
      { asin: 'B0PX4KPROJ', title: 'Sensei Beam Portable 4K', share: '83.5%', conversion: '0.57%' },
      { asin: 'B0LG1MINI4', title: 'LumenGo Pocket Pro', share: '61.9%', conversion: '0.31%' },
      { asin: 'B0TK4CARRY', title: 'CarryCase Projector Kit', share: '44.7%', conversion: '0.19%' },
    ],
  }),
  buildRow('row-10', {
    keyword: 'ergonomic office chair',
    dailyChange: '+28.44%',
    weeklyChange: '+13%',
    monthlyChange: '+6%',
    trend: 'STABLE',
    topCategory: 'Office Products',
    orders: 2,
    date: '24.09.2025',
    series: 'Workspace',
    detailItems: [
      { asin: 'B0ERGOCHA1', title: 'Sensei FlowMesh Chair', share: '57.4%', conversion: '0.41%' },
      { asin: 'B0LMBERLUM', title: 'LumbarSync Pro', share: '43.6%', conversion: '0.33%' },
      { asin: 'B0PADARMST', title: 'Adjustable Arm Pad Kit', share: '28.1%', conversion: '0.18%' },
    ],
  }),
  buildRow('row-11', {
    keyword: 'compostable trash bags',
    dailyChange: '+35.12%',
    weeklyChange: '+16%',
    monthlyChange: '+7%',
    trend: 'RISING',
    topCategory: 'Home & Kitchen',
    orders: 4,
    date: '25.09.2025',
    series: 'Eco adoption',
    detailItems: [
      { asin: 'B0ECOBAG45', title: 'Sensei BioBag 13 Gallon', share: '69.3%', conversion: '0.54%' },
      { asin: 'B0GREENLIN', title: 'GreenLoop Compost Liners', share: '51.0%', conversion: '0.37%' },
      { asin: 'B0CNTRYEAR', title: 'EarthWrap Bag Pack', share: '33.4%', conversion: '0.22%' },
    ],
  }),
  buildRow('row-12', {
    keyword: 'stick vac cordless',
    dailyChange: '+31.44%',
    weeklyChange: '+15%',
    monthlyChange: '+5%',
    trend: 'RISING',
    topCategory: 'Home & Kitchen',
    orders: 3,
    date: '25.09.2025',
    series: 'Appliance refresh',
    detailItems: [
      { asin: 'B0STICKVAC', title: 'Sensei AeroStick Max', share: '74.2%', conversion: '0.48%' },
      { asin: 'B0LIGHTVAC', title: 'FeatherLite Cordless Vac', share: '59.8%', conversion: '0.36%' },
      { asin: 'B0PETADAPT', title: 'PetAdapt Brush Kit', share: '37.5%', conversion: '0.23%' },
    ],
  }),
  buildRow('row-13', {
    keyword: 'sleep headphones bluetooth',
    dailyChange: '+56.02%',
    weeklyChange: '+22%',
    monthlyChange: '+9%',
    trend: 'RISING',
    topCategory: 'Electronics',
    orders: 2,
    date: '27.09.2025',
    series: 'Relaxation tech',
    detailItems: [
      { asin: 'B0SLEEPBND', title: 'DreamBand Sleep Headphones', share: '81.2%', conversion: '0.52%' },
      { asin: 'B0AUDIOEYE', title: 'AudioEye Night Mask', share: '62.5%', conversion: '0.34%' },
      { asin: 'B0WHITEZON', title: 'WhiteZone Sound Pillow', share: '41.1%', conversion: '0.19%' },
    ],
  }),
  buildRow('row-14', {
    keyword: 'pet hair remover roller',
    dailyChange: '+29.84%',
    weeklyChange: '+11%',
    monthlyChange: '+4%',
    trend: 'STABLE',
    topCategory: 'Pet Supplies',
    orders: 3,
    date: '24.09.2025',
    series: 'Pet care',
    detailItems: [
      { asin: 'B0PETROLL', title: 'Sensei FurLift Roller', share: '68.3%', conversion: '0.47%' },
      { asin: 'B0HAIRLESS', title: 'HairLess Pro Remover', share: '52.8%', conversion: '0.33%' },
      { asin: 'B0CATCLEAN', title: 'CatClean Sticky Pad', share: '34.9%', conversion: '0.18%' },
    ],
  }),
  buildRow('row-15', {
    keyword: 'zero gravity chair outdoor',
    dailyChange: '+33.18%',
    weeklyChange: '+14%',
    monthlyChange: '+6%',
    trend: 'RISING',
    topCategory: 'Garden & Outdoor',
    orders: 2,
    date: '24.09.2025',
    series: 'Seasonal outdoor',
    detailItems: [
      { asin: 'B0ZEROGRV', title: 'Sensei Outdoor Zero Gravity Set', share: '71.4%', conversion: '0.49%' },
      { asin: 'B0SUNLOUN', title: 'SunLounge Mesh Chair', share: '55.2%', conversion: '0.31%' },
      { asin: 'B0CUPCOOL', title: 'CoolCup Arm Rest', share: '32.6%', conversion: '0.16%' },
    ],
  }),
  buildRow('row-16', {
    keyword: 'mini pc ryzen',
    dailyChange: '+41.28%',
    weeklyChange: '+19%',
    monthlyChange: '+11%',
    trend: 'RISING',
    topCategory: 'Electronics',
    orders: 4,
    date: '27.09.2025',
    series: 'Computing pulse',
    detailItems: [
      { asin: 'B0RYZENBOX', title: 'Sensei Cube Ryzen Mini PC', share: '85.7%', conversion: '0.48%' },
      { asin: 'B0DESKPRO', title: 'DeskPro Ryzen Station', share: '64.9%', conversion: '0.33%' },
      { asin: 'B0MINIMEM', title: 'MiniMem Upgrade Kit', share: '42.5%', conversion: '0.21%' },
    ],
  }),
  buildRow('row-17', {
    keyword: 'espresso machine compact',
    dailyChange: '+37.92%',
    weeklyChange: '+17%',
    monthlyChange: '+5%',
    trend: 'RISING',
    topCategory: 'Home & Kitchen',
    orders: 3,
    date: '25.09.2025',
    series: 'Coffee trend',
    detailItems: [
      { asin: 'B0ESPRSCM', title: 'Sensei Compact Espresso', share: '70.4%', conversion: '0.58%' },
      { asin: 'B0BARISTAK', title: 'BaristaK Manual Frother', share: '47.8%', conversion: '0.34%' },
      { asin: 'B0CUPWARM', title: 'CupWarm Dock', share: '28.9%', conversion: '0.17%' },
    ],
  }),
  buildRow('row-18', {
    keyword: 'rgb desk light bar',
    dailyChange: '+43.22%',
    weeklyChange: '+21%',
    monthlyChange: '+10%',
    trend: 'RISING',
    topCategory: 'Electronics',
    orders: 5,
    date: '26.09.2025',
    series: 'Gaming ambiance',
    detailItems: [
      { asin: 'B0RGBLIGHT', title: 'Sensei GlowBar Kit', share: '82.6%', conversion: '0.49%' },
      { asin: 'B0NEONARC', title: 'NeonArc Desk Lights', share: '55.1%', conversion: '0.31%' },
      { asin: 'B0SYNCAPP', title: 'SyncApp Control Hub', share: '33.5%', conversion: '0.18%' },
    ],
  }),
  buildRow('row-19', {
    keyword: 'kids coding kit',
    dailyChange: '+34.64%',
    weeklyChange: '+15%',
    monthlyChange: '+6%',
    trend: 'RISING',
    topCategory: 'Toys & Games',
    orders: 4,
    date: '24.09.2025',
    series: 'STEM growth',
    detailItems: [
      { asin: 'B0CODEKIT', title: 'Sensei Kids Coding Kit', share: '66.7%', conversion: '0.58%' },
      { asin: 'B0ROBOBUD', title: 'RoboBuddy Starter Pack', share: '52.9%', conversion: '0.41%' },
      { asin: 'B0PUZZLED', title: 'PuzzleLogic Expansion', share: '36.3%', conversion: '0.24%' },
    ],
  }),
  buildRow('row-20', {
    keyword: 'organic baby formula',
    dailyChange: '+26.48%',
    weeklyChange: '+12%',
    monthlyChange: '+4%',
    trend: 'STABLE',
    topCategory: 'Grocery',
    orders: 2,
    date: '23.09.2025',
    series: 'Nutrition trend',
    detailItems: [
      { asin: 'B0BABFORM', title: 'Sensei Organic Infant Formula', share: '74.6%', conversion: '0.63%' },
      { asin: 'B0PUREMIX', title: 'PureMix Goat Formula', share: '59.5%', conversion: '0.52%' },
      { asin: 'B0VITBOOST', title: 'VitBoost Prebiotic Drops', share: '41.7%', conversion: '0.33%' },
    ],
  }),
  buildRow('row-21', {
    keyword: 'vegan protein powder',
    dailyChange: '+32.75%',
    weeklyChange: '+14%',
    monthlyChange: '+5%',
    trend: 'RISING',
    topCategory: 'Health & Household',
    orders: 4,
    date: '26.09.2025',
    series: 'Supplement pulse',
    detailItems: [
      { asin: 'B0VEGXPRO', title: 'PlantFusion Sensei Blend', share: '69.4%', conversion: '0.54%' },
      { asin: 'B0PEALEAN', title: 'PeaLean Performance', share: '52.2%', conversion: '0.37%' },
      { asin: 'B0SHAKERFX', title: 'ShakerFlex Bottle', share: '34.2%', conversion: '0.22%' },
    ],
  }),
  buildRow('row-22', {
    keyword: 'wireless carplay adapter',
    dailyChange: '+36.18%',
    weeklyChange: '+15%',
    monthlyChange: '+7%',
    trend: 'RISING',
    topCategory: 'Automotive',
    orders: 5,
    date: '25.09.2025',
    series: 'Automotive tech',
    detailItems: [
      { asin: 'B0CARPLAY', title: 'Sensei Wireless CarPlay', share: '82.5%', conversion: '0.46%' },
      { asin: 'B0ADAPTUN', title: 'AdaptUnit USB-C Adapter', share: '56.4%', conversion: '0.29%' },
      { asin: 'B0CARMOUNT', title: 'MagMount Vent Holder', share: '39.8%', conversion: '0.18%' },
    ],
  }),
  buildRow('row-23', {
    keyword: 'diy candle kit',
    dailyChange: '+22.44%',
    weeklyChange: '+10%',
    monthlyChange: '+4%',
    trend: 'STABLE',
    topCategory: 'Arts, Crafts & Sewing',
    orders: 3,
    date: '22.09.2025',
    series: 'Hobby radar',
    detailItems: [
      { asin: 'B0CANDLET', title: 'Sensei DIY Candle Lab', share: '64.8%', conversion: '0.49%' },
      { asin: 'B0SOYWAXKT', title: 'SoyWax Studio Kit', share: '47.1%', conversion: '0.33%' },
      { asin: 'B0SCENTKIT', title: 'ScentCraft Oil Set', share: '31.8%', conversion: '0.19%' },
    ],
  }),
  buildRow('row-24', {
    keyword: 'gaming handheld case',
    dailyChange: '+27.11%',
    weeklyChange: '+11%',
    monthlyChange: '+5%',
    trend: 'RISING',
    topCategory: 'Video Games',
    orders: 2,
    date: '23.09.2025',
    series: 'Accessory spike',
    detailItems: [
      { asin: 'B0HANDCASE', title: 'Sensei Handheld Vault', share: '77.6%', conversion: '0.44%' },
      { asin: 'B0SHELLPRO', title: 'ShellPro Rugged Case', share: '58.3%', conversion: '0.28%' },
      { asin: 'B0SCREENFX', title: 'ScreenFX Protector Pack', share: '36.9%', conversion: '0.17%' },
    ],
  }),
  buildRow('row-25', {
    keyword: 'wireless lavalier mic',
    dailyChange: '+39.42%',
    weeklyChange: '+20%',
    monthlyChange: '+9%',
    trend: 'RISING',
    topCategory: 'Electronics',
    orders: 4,
    date: '26.09.2025',
    series: 'Creator tools',
    detailItems: [
      { asin: 'B0LAVSET12', title: 'Sensei Lavalier Duo', share: '84.9%', conversion: '0.51%' },
      { asin: 'B0MICCLIP', title: 'ClipMic Broadcast Pack', share: '62.1%', conversion: '0.33%' },
      { asin: 'B0WINDSCRN', title: 'WindScreen Foam Kit', share: '33.6%', conversion: '0.18%' },
    ],
  }),
]
